import { Platform } from '..'
import { User } from '../user/user'
import { ID as AccountID } from '../account/id'
import { AccessLost } from '../account/account'
import { CurrencyCode } from '../currency'

export namespace Team {
    /**
     * @name ID
     * @description Group id, signed int, length 11
     */
    export type ID = number

    /**
     * @name Avatar
     * @description Whether a team is an agency or a business
     */
    export type Avatar = 'agency' | 'advertiser' | 'unspecified'

    // Legacy is regular users, system is for system (api only) users
    export type Type = 'legacy' | 'system'

    /**
     * @name OpteoLifeCycleStage
     * @description The stage of the team in its lifecycle, eg "trialing" or "mature_customer"
     */
    export enum OpteoLifeCycleStage {
        DEAD_GROUP = 'dead_group',
        TEST_ACCOUNT = 'test_account',
        NO_STRIPE_CUSTOMER = 'no_stripe_customer',
        CREATED_ACCOUNT = 'created_account',
        CONNECTED_AW = 'connected_aw',
        TRIALER = 'trialer',
        UNLOCKED_TRIALER = 'unlocked_trialer',
        UNPAID = 'unpaid',
        RETRIALER = 'retrialer',
        YOUNG_CUSTOMER = 'young_customer',
        MATURE_CUSTOMER = 'mature_customer',
        CANCELLED_TRIAL = 'cancelled_trial',
        CANCELLED_CUSTOMER = 'cancelled_customer',
        CANCELLED_AT_PERIOD_END_TRIAL = 'cancelled_at_period_end_trial',
        CANCELLED_AT_PERIOD_END_CUSTOMER = 'cancelled_at_period_end_customer',
        FREE_USER = 'free_user',
    }

    /**
     * @name Member
     * @description Reduced version of User.Info for use in Linked Accounts
     */
    export interface Member {
        userId: User.ID
        name: string
        email: string
        lastLogin: Date | null
        isAdmin: boolean
        profileUrl: string | null
        status: string
    }

    /**
     * @name Account
     * @description Reduced version of Account.Info for use in Linked Accounts
     */
    export interface Account {
        accountId: AccountID
        name: string
        initials: string
        platform: Platform.Platform
        // This refers to the primary id of the account used in the corresponding platform
        // Google -> Account Customer ID (string)
        // Microsoft -> Account ID (number)
        platformId: string | number
        active: boolean
        color: string
        spend30d: number
        // 'fetching': no metrics yet, a sync is queued. 'unavailable': the last sync failed.
        spendStatus: 'available' | 'fetching' | 'unavailable'
        spendUpdatedAt: Date | null
        lastLogin: number // Stored as unix timestamp to allow sorting in oTable, where "null" (aka never logged into account) is 0
        currencyCode: CurrencyCode
        connectedToOpteoTs: Date
        teamMembers: (Pick<Member, 'userId' | 'name' | 'email' | 'isAdmin' | 'profileUrl'> & {
            active: boolean
        })[]
        availableConnections: {
            isCurrent: boolean
            connectionId: number
            loginCustomerId: string
            email: string
        }[]
        isUnlinkable?: boolean
        accessLost?: AccessLost | null
    }
}
